import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Terminal, Send, ShieldAlert, RefreshCw } from 'lucide-react';
import { PageHeader, Section } from '../components/Scaffold';
import api from '../api/client';
import useAuthStore from '../stores/authStore';

/**
 * Remote command console — run a command on a live kiosk PC and see its output,
 * so a client machine can be debugged without walking up to it / using a test PC.
 *
 * Backend: internal_remote_commands.py at /api/internal/pcs/{cafe_id}/… — the
 * café-less route. The café dashboard's /api/command/send reads the café off the
 * token, which a SuperAdmin-portal token has none of, so it 400'd for EVERYONE and
 * this page never worked. `run_command` is superadmin / write-platform-account only and,
 * when "Run as administrator" is on, executes as SYSTEM via the kiosk's PrimusElevator
 * (which fetches the command text from the backend itself — the kiosk never hands a
 * command string to SYSTEM). Output comes back in the command ACK result and is shown
 * here by polling the per-PC command history.
 */
const STATUS_BADGE = { SUCCEEDED: 'success', PENDING: 'warning', RUNNING: 'info', FAILED: 'danger' };

// A PC is named by "<cafe_id>:<pc_id>": ids restart at 1 in every café's own DB.
const keyOf = (pc) => `${pc.cafe_id ?? ''}:${pc.id}`;
const parseKey = (key) => {
  const [cafe, pc] = String(key).split(':');
  return { cafeId: cafe === '' ? null : Number(cafe), pcId: Number(pc) };
};

export default function RemoteCommands() {
  const { canSeeAsSuperAdmin, canActAsSuperAdmin } = useAuthStore();
  const canView = canSeeAsSuperAdmin();
  const canRun = canActAsSuperAdmin();

  const [pcs, setPcs] = useState([]);
  const [target, setTarget] = useState('');
  const [commandLine, setCommandLine] = useState('');
  const [runInCmd, setRunInCmd] = useState(true);
  const [elevated, setElevated] = useState(true);
  const [workingDir, setWorkingDir] = useState('');
  const [timeout, setTimeoutSec] = useState(60);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const pollRef = useRef(null);

  // Fleet-wide first, per-café as a fallback. GET /clientpc/ is café-scoped and 400s
  // for a SuperAdmin token (no cafe_id on it), which is why this page showed
  // "Failed to load PCs" and an empty dropdown for every superadmin — it was unusable,
  // and nobody noticed because the nav entry was removed in 2026-07. PCRegistry.jsx
  // already documents the same trap and uses the global counterpart; this now matches
  // it, while keeping the old call as a fallback for tiers whose backend predates
  // /internal/fleet/pcs (it 404s there rather than returning a list).
  useEffect(() => {
    let alive = true;
    api.get('/internal/fleet/pcs')
      .then((r) => { if (alive) setPcs(r.data || []); })
      .catch(() => api.get('/clientpc/')
        .then((r) => { if (alive) setPcs(r.data || []); })
        .catch(() => { if (alive) setError('Failed to load PCs.'); }));
    return () => { alive = false; if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const pcLabel = useCallback((pc) => {
    const name = pc.name || pc.pc_name || pc.hostname || `PC-${pc.id}`;
    const cafe = pc.cafe_name || (pc.cafe_id != null ? `Café ${pc.cafe_id}` : '');
    return cafe ? `${name} · ${cafe}` : name;
  }, []);

  const loadHistory = useCallback(async (key) => {
    if (!key) return;
    const { cafeId, pcId } = parseKey(key);
    try {
      // The café-scoped route stays as a fallback for a list that came from
      // GET /clientpc/ (no café on it).
      const r = await api.get(cafeId != null ? `/internal/pcs/${cafeId}/history/${pcId}` : `/command/history/${pcId}`);
      setHistory((r.data || []).filter((c) => c.command === 'run_command'));
    } catch {
      /* leave previous history on transient error */
    }
  }, []);

  const dispatch = async () => {
    setError(null);
    if (!target) { setError('Pick a target PC.'); return; }
    const { cafeId, pcId } = parseKey(target);
    if (!commandLine.trim()) { setError('Enter a command.'); return; }
    setDispatching(true);
    try {
      const params = {
        command_line: commandLine.trim(),
        run_in_cmd: runInCmd,
        elevated,
        timeout: Number(timeout) || 60,
      };
      if (workingDir.trim()) params.working_directory = workingDir.trim();
      await api.post(cafeId != null ? `/internal/pcs/${cafeId}/command` : '/command/send', {
        pc_id: pcId,
        command: 'run_command',
        params: JSON.stringify(params),
      });
      // Poll history so the result/output appears as the kiosk ACKs.
      await loadHistory(target);
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(() => loadHistory(target), 2000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Dispatch failed.');
    } finally {
      setDispatching(false);
    }
  };

  // Stop polling once the most recent command reaches a terminal state.
  useEffect(() => {
    const latest = history[0];
    if (latest && (latest.state === 'SUCCEEDED' || latest.state === 'FAILED') && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [history]);

  const outputOf = (result) => {
    if (!result || typeof result !== 'object') return '';
    if (result.output) return result.output;        // elevated path
    const parts = [];
    if (result.stdout) parts.push(result.stdout);    // user-context path
    if (result.stderr) parts.push('[stderr]\n' + result.stderr);
    if (result.reason) parts.push('[error] ' + result.reason);
    if (result.exit_code != null) parts.push(`[exit=${result.exit_code}]`);
    return parts.join('\n');
  };

  const latest = useMemo(() => history[0], [history]);

  if (!canView) {
    return (
      <div className="page">
        <PageHeader title="Remote Commands" subtitle="Run a command on a kiosk PC" />
        <div className="card" style={{ padding: 24, display: 'flex', gap: 12, alignItems: 'center' }}>
          <ShieldAlert size={20} />
          <span>Remote commands are for the platform team only.</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page remote-commands-page">
      <style>{`
        .remote-commands-page {
          /* noLag rebrand: scoped to this page only. No Figma node exists
             for Remote Commands — applied by the same accent-only pattern
             as the other scaffold pages this session. STATUS_BADGE
             (SUCCEEDED/PENDING/RUNNING/FAILED) is a separate --status-*
             token, untouched. */
          --accent-primary: #FE7B00;
          --accent-primary-rgb: 254, 123, 0;
          --accent-primary-glow: rgba(254, 123, 0, 0.25);
          --accent-primary-subtle: rgba(254, 123, 0, 0.12);
          --accent-gradient: linear-gradient(135deg, #FE7B00 0%, #F7302F 100%);
        }
      `}</style>
      <PageHeader title="Remote Commands" subtitle="Run a command on a live kiosk PC and see its output" />

      <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
        <Section title="Run a command">
          {error && (
            <div className="card" style={{ padding: 12, marginBottom: 12, color: 'var(--danger, #f87171)' }}>{error}</div>
          )}
          <div className="form-grid">
            <div className="field">
              <label className="field__label">Target PC</label>
              <select className="field__select" value={target} onChange={(e) => { setTarget(e.target.value); setHistory([]); loadHistory(e.target.value); }}>
                <option value="">Select a PC…</option>
                {pcs.map((pc) => (
                  <option key={keyOf(pc)} value={keyOf(pc)}>{pcLabel(pc)}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label className="field__label">Command</label>
              <input
                className="field__input font-mono"
                value={commandLine}
                onChange={(e) => setCommandLine(e.target.value)}
                placeholder='e.g. ipconfig /all   or   powershell -Command "Get-Service vgc"'
              />
            </div>
            <div className="field">
              <label className="field__label">Working directory (optional)</label>
              <input className="field__input" value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} placeholder="C:\\" />
            </div>
            <div className="field">
              <label className="field__label">Timeout (seconds)</label>
              <input className="field__input" type="number" min="1" max="600" value={timeout} onChange={(e) => setTimeoutSec(e.target.value)} />
            </div>
          </div>
          <div style={{ display: 'flex', gap: 20, marginTop: 12, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={elevated} onChange={(e) => setElevated(e.target.checked)} />
              Run as administrator (SYSTEM)
            </label>
            <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="checkbox" checked={runInCmd} onChange={(e) => setRunInCmd(e.target.checked)} />
              Run in cmd (/c)
            </label>
          </div>
          <div className="page-actions" style={{ marginTop: 16 }}>
            <button className="btn btn--primary" onClick={dispatch} disabled={dispatching || !canRun}
              title={canRun ? undefined : 'Read-only access: you can view results but not run commands'}>
              <Send size={16} /> {dispatching ? 'Dispatching…' : 'Run command'}
            </button>
            {!canRun && <span className="text-secondary" style={{ marginLeft: 12 }}>Read-only access — you can view results but not run commands.</span>}
          </div>
        </Section>
      </div>

      {latest && (
        <div className="card" style={{ marginBottom: 'var(--space-6)' }}>
          <Section title="Latest result">
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
              <span className={`badge badge--${STATUS_BADGE[latest.state] || 'info'}`}>{latest.state}</span>
              {(!latest.state || latest.state === 'PENDING' || latest.state === 'RUNNING') && (
                <span className="text-secondary" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <RefreshCw size={14} className="spin" /> waiting for the kiosk…
                </span>
              )}
            </div>
            <pre style={{
              background: 'var(--surface-2, #0b0b12)', padding: 14, borderRadius: 8, overflow: 'auto',
              maxHeight: 360, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0,
            }}>{outputOf(latest.result) || '(no output yet)'}</pre>
          </Section>
        </div>
      )}

      <Section title="Recent run_command history (this PC)">
        <div className="card table-card">
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr><th>When</th><th>Command</th><th>Status</th></tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan={3} className="text-tertiary">No run_command history for this PC yet.</td></tr>
              ) : history.map((h) => {
                let cl = '';
                try { cl = JSON.parse(h.params || '{}').command_line || ''; } catch { /* ignore */ }
                return (
                  <tr key={h.id}>
                    <td className="text-tertiary">{h.issued_at ? new Date(h.issued_at).toLocaleString() : '—'}</td>
                    <td className="font-mono"><Terminal size={14} /> {cl}</td>
                    <td><span className={`badge badge--${STATUS_BADGE[h.state] || 'info'}`}>{h.state}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      </Section>
    </div>
  );
}
